import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft, ArrowRight } from "lucide-react";
import StageStepper from "@/components/StageStepper";
import Stage0 from "@/stages/Stage0";
import Stage1 from "@/stages/Stage1";
import Stage2 from "@/stages/Stage2";
import Stage3 from "@/stages/Stage3";
import Stage4 from "@/stages/Stage4";
import Stage5 from "@/stages/Stage5";
import Stage10 from "@/stages/Stage10";
import Stage6 from "@/stages/Stage6";
import Stage7 from "@/stages/Stage7";
import Stage8 from "@/stages/Stage8";
import Stage9 from "@/stages/Stage9";
import { api, STAGES } from "@/lib/api";

const VIEWS = { 0: Stage0, 1: Stage1, 2: Stage2, 3: Stage3, 4: Stage4, 5: Stage5, 6: Stage6, 7: Stage7, 8: Stage8, 9: Stage9, 10: Stage10 };

export default function JobPage() {
  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [view, setView] = useState(null);

  const refresh = useCallback(async (follow) => {
    const { data } = await api.get(`/jobs/${jobId}`);
    setJob(data);
    setView((v) => (v === null || follow ? data.current_stage : v));
  }, [jobId]);

  useEffect(() => { setView(null); refresh(); }, [refresh]);
  const anyRunning = job && Object.values(job.stages).some((s) => s.status === "running");
  useEffect(() => {
    if (!anyRunning) return undefined;
    const t = setInterval(() => refresh(), 6000);
    return () => clearInterval(t);
  }, [anyRunning, refresh]);

  if (!job || view === null) return <div className="p-10 text-muted-foreground">Loading…</div>;
  const c = job.client;
  const View = VIEWS[view];
  return (
    <div className="px-8 py-6 max-w-[1600px]">
      <Link to="/clients" className="text-xs text-muted-foreground hover:text-slate-200 inline-flex items-center gap-1 mb-3"><ChevronLeft size={12} /> All clients</Link>
      <div className="flex flex-wrap items-end gap-4 mb-5">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight" data-testid="job-client-name">{c.name}</h1>
          <div className="text-sm text-muted-foreground mt-1 flex flex-wrap gap-3">
            <span className="px-2 py-0.5 rounded border border-blue-500/40 text-blue-300 text-xs font-mono">{c.visa_status || "Visa ?"}{c.visa_expiry ? ` · until ${c.visa_expiry}` : ""}</span>
            <span>{c.current_location}</span><span>·</span><span>{c.seniority}</span>
          </div>
        </div>
        <div className="ml-auto flex gap-2">
          {job.sibling_jobs.map((s) => (
            <Link key={s.id} to={`/jobs/${s.id}`} data-testid={`track-tab-${s.id}`}
              className={`px-3 py-1.5 rounded-lg text-sm border ${s.id === job.id ? "border-blue-500/60 bg-blue-600/15 text-blue-200" : "border-border text-slate-400 hover:text-slate-200"}`}>
              Track: {s.track_title}
            </Link>
          ))}
        </div>
      </div>
      <div className="card-surface mb-6 py-4 sticky top-0 z-20 backdrop-blur-xl bg-card/90"><StageStepper job={job} viewing={view} onSelect={setView} /></div>
      {view !== job.current_stage && (
        <div className="mb-5 rounded-lg border border-blue-500/30 bg-blue-500/5 px-4 py-2.5 text-sm flex items-center gap-3" data-testid="viewing-other-step-banner">
          <span>You're looking at a {STAGES.findIndex((s) => s.id === view) < STAGES.findIndex((s) => s.id === job.current_stage) ? "finished" : "later"} step. The client is on <b>{STAGES.find((s) => s.id === job.current_stage)?.name}</b>.</span>
          <button data-testid="go-current-step" onClick={() => setView(job.current_stage)} className="ml-auto text-blue-300 hover:text-blue-200 inline-flex items-center gap-1">Go to current step <ArrowRight size={13} /></button>
        </div>
      )}
      <View key={`${job.id}-${view}`} job={job} refresh={refresh} />
    </div>
  );
}
